/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.controlsElement = null;
    this.resizeObserver = null;
    this.imageLoadListeners = [];
    this.alignFrame = null;
    this.resizeTimeout = null;
    this.originalSlides = null;
    this.isInitialized = false;
    this._bulletClickBound = false;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) return;
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();
    this.syncControlsVisibility(shouldBeActive);

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
      this.scheduleNavigationAlignment();
    }, 150);
  }

  scheduleNavigationAlignment() {
    if (this.alignFrame) cancelAnimationFrame(this.alignFrame);
    this.alignFrame = requestAnimationFrame(() => {
      this.alignNavigationToProductImage();
      this.alignFrame = null;
    });
  }

  /**
   * Walk the offsetParent chain to get an element's top offset relative to
   * this.element. Unlike getBoundingClientRect(), offsetTop ignores CSS
   * transforms (animations), giving us the element's resting layout position.
   */
  getOffsetTop(el) {
    let top = 0;
    let current = el;
    while (current && current !== this.element) {
      top += current.offsetTop;
      current = current.offsetParent;
    }
    return top;
  }

  /**
   * Align nav overlay to the image area midpoint instead of the full card height.
   * Uses offset-based positioning to avoid stale measurements during CSS
   * entry animations (fade-up, etc.).
   */
  alignNavigationToProductImage() {
    if (!this.isInitialized) return;

    const controlsElement = this.controlsElement || this.element.querySelector('[data-slider-control]');
    if (!controlsElement) return;

    const navOverlay = controlsElement.querySelector('.slider-nav-hover');
    if (!navOverlay) return;

    const imageContainer = this.element.querySelector('[data-product-card] .content-image');
    if (!imageContainer) {
      navOverlay.style.removeProperty('top');
      return;
    }

    const imageTop = this.getOffsetTop(imageContainer);
    const imageMidpoint = imageTop + (imageContainer.offsetHeight / 2);

    navOverlay.style.top = `${Math.round(imageMidpoint)}px`;
    this.updateNavigationHorizontalPositioning();
  }

  updateNavigationHorizontalPositioning() {
    const controlsElement = this.controlsElement || this.element.querySelector('[data-slider-control]');
    if (!controlsElement) return;

    const navOverlay = controlsElement.querySelector('.slider-nav-hover');
    const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
    const nextBtn = controlsElement.querySelector('.slider-navigation-next');
    if (!navOverlay || !prevBtn || !nextBtn) return;

    const sliderRect = this.element.getBoundingClientRect();
    const prevHalf = prevBtn.getBoundingClientRect().width / 2;
    const nextHalf = nextBtn.getBoundingClientRect().width / 2;
    const spaceLeft = sliderRect.left;
    const spaceRight = window.innerWidth - sliderRect.right;

    // Keep arrows fully inside on mobile, or if edge space is too tight.
    const shouldKeepInside =
      window.innerWidth < MOBILE_BREAKPOINT ||
      spaceLeft < prevHalf ||
      spaceRight < nextHalf;

    prevBtn.style.marginLeft = shouldKeepInside ? '0px' : `-${Math.round(prevHalf)}px`;
    nextBtn.style.marginRight = shouldKeepInside ? '0px' : `-${Math.round(nextHalf)}px`;
  }

  /**
   * Set up observers that re-align navigation when the image or slider
   * dimensions change (image load, responsive reflow, etc.).
   */
  setupImageAlignmentObservers() {
    this.teardownImageAlignmentObservers();

    const imageContainer = this.element.querySelector('[data-product-card] .content-image');
    if (!imageContainer) return;

    // Single ResizeObserver watching both the image container and the slider
    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleNavigationAlignment());
      this.resizeObserver.observe(imageContainer);
      this.resizeObserver.observe(this.element);
    }

    // Catch lazy-loaded images that haven't finished loading yet
    imageContainer.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      const handler = () => this.scheduleNavigationAlignment();
      img.addEventListener('load', handler, { once: true });
      this.imageLoadListeners.push({ img, handler });
    });
  }

  teardownImageAlignmentObservers() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.imageLoadListeners.forEach(({ img, handler }) => {
      img.removeEventListener('load', handler);
    });
    this.imageLoadListeners = [];
  }

  /**
   * Build the renderCustom callback for custom bar-style pagination bullets
   * and attach a delegated click handler (once).
   */
  buildCustomPaginationRenderer(colour) {
    if (!this._bulletClickBound) {
      this._bulletClickBound = true;
      this.element.addEventListener('click', (e) => {
        const bullet = e.target.closest('[data-slide-index]');
        if (bullet && this.swiper) {
          this.swiper.slideTo(parseInt(bullet.dataset.slideIndex, 10) - 1);
        }
      });
    }

    return (_swiper, current, total) => {
      let bullets = '';
      for (let i = 1; i <= total; i++) {
        const activeClass = i === current ? 'opacity-100' : 'opacity-50';
        bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${activeClass}" style="background-color: ${colour};" data-slide-index="${i}"></span>`;
      }
      return `<div class="flex gap-2 justify-center">${bullets}</div>`;
    };
  }

  /**
   * Find controls associated with this slider (nested or linked by slider id).
   */
  resolveControlsElement() {
    if (this.controlsElement && document.contains(this.controlsElement)) {
      return this.controlsElement;
    }

    const blockId = this.element.dataset.blockId;
    let controlsElement = this.element.querySelector('[data-slider-control]');

    if (!controlsElement && blockId) {
      const section = this.element.closest('[id^="shopify-section-"]');
      if (section) {
        controlsElement = section.querySelector(`[data-slider-control][data-slider-id="${blockId}"]`);
      }

      if (!controlsElement) {
        const allControls = section?.querySelectorAll('[data-slider-control]:not([data-slider-id])');
        if (allControls && allControls.length === 1) {
          const allSliders = section?.querySelectorAll('[data-slider-block]');
          if (allSliders && allSliders.length === 1) {
            controlsElement = allControls[0];
          }
        }
      }
    }

    this.controlsElement = controlsElement || null;
    return this.controlsElement;
  }

  /**
   * Controls should only be visible when the slider is active for the viewport.
   */
  syncControlsVisibility(shouldBeActive) {
    const controlsElement = this.resolveControlsElement();
    if (!controlsElement) return;

    controlsElement.classList.toggle('hidden', !shouldBeActive);
    controlsElement.setAttribute('aria-hidden', shouldBeActive ? 'false' : 'true');
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.slider-content');
    if (!this.contentEl) return;

    // Find and preserve control elements before clearing
    const blockId = this.element.dataset.blockId;
    const controlsElement = this.resolveControlsElement();

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, controls, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => 
        !excludedTags.includes(el.tagName) && 
        !el.hasAttribute('data-slider-control')
      );
      
      if (slides.length < 1) {
        return;
      }

      this.originalSlides = slides;
    }

    // Capture whether controls live inside .slider-content BEFORE clearing it
    const wasNested = controlsElement && this.contentEl.contains(controlsElement);

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper', 'w-full', 'max-w-full');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);
    
    // Re-append controls if they were nested inside .slider-content
    if (wasNested) {
      this.element.appendChild(controlsElement);
    }

    // Get options from data attribute
    const customOptions = this.element.dataset.swiperOptions 
      ? JSON.parse(this.element.dataset.swiperOptions) 
      : {};

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 16,
      // Touch / drag
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchReleaseOnEdges: false,
    };

    const customPaginationColour = this.element.dataset.paginationColour;

    // Only enable loop if more than 1 slide
    if (slides.length > 1 && customOptions.loop !== false) {
      defaultOptions.loop = true;
    }

    // Add navigation selectors (blockId already defined earlier)
    if (blockId && customOptions.navigation !== false) {
      let navSelector;
      
      if (controlsElement) {
        // Controls exist - use them for navigation
        if (controlsElement.dataset.sliderId === blockId) {
          // Controls are separate and explicitly linked
          navSelector = `[data-slider-control][data-slider-id="${blockId}"]`;
        } else if (!controlsElement.dataset.sliderId) {
          // Controls are nested or auto-matched – pass the actual button elements
          const nextBtn = controlsElement.querySelector('.slider-navigation-next');
          const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
          if (nextBtn && prevBtn) {
            customOptions.navigation = {
              nextEl: nextBtn,
              prevEl: prevBtn,
            };
          }
        }
      }
      
      // If navSelector is set (separate controls), use selector strings
      if (navSelector) {
        customOptions.navigation = {
          nextEl: `${navSelector} .slider-navigation-next`,
          prevEl: `${navSelector} .slider-navigation-prev`,
        };
      }
    }

    // Configure pagination
    let paginationColour = customPaginationColour;
    
    if (controlsElement) {
      const paginationEl = controlsElement.querySelector('.swiper-pagination');
      if (paginationEl) {
        const paginationType = paginationEl.dataset.paginationType || 'bullets';
        paginationColour = paginationEl.dataset.paginationColour || customPaginationColour;
        
        customOptions.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: true,
        };
        
        if (paginationType === 'custom') {
          customOptions.pagination.renderCustom = this.buildCustomPaginationRenderer(paginationColour);
        }
      }
    } else if (customOptions.pagination?.type === 'custom') {
      customOptions.pagination.renderCustom = this.buildCustomPaginationRenderer(customPaginationColour);
    }

    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, {
      ...defaultOptions,
      ...customOptions,
    });

    this.isInitialized = true;
    this.setupImageAlignmentObservers();

    // Immediate alignment – uses offsetTop so CSS transforms (entry animations) are ignored
    this.alignNavigationToProductImage();

    // Re-align after web fonts finish loading (can shift text-based layout)
    document.fonts?.ready?.then(() => this.scheduleNavigationAlignment());
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.controlsElement = null;
    this.teardownImageAlignmentObservers();
    if (this.alignFrame) {
      cancelAnimationFrame(this.alignFrame);
      this.alignFrame = null;
    }
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  // Clean up instances whose elements were removed from the DOM
  // (e.g. after a product-feed collection switch replaces the markup)
  sliderInstances.forEach((instance, element) => {
    if (!document.contains(element)) {
      instance.cleanup();
      sliderInstances.delete(element);
      initializedSliders.delete(element);
    }
  });

  document.querySelectorAll('[data-slider-block]').forEach(slider => {
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation (bfcache restore)
window.addEventListener('pageshow', (e) => {
  if (e.persisted) initSliderBlocks();
});

// Export for external use
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;
